/// <reference path='../DataTypes/types.js' />

import SettingsService from './SettingsService';
import DateService from './DateService';
import AuthenticationService from './AuthenticationService';

export function addVat(price) {
  return price * (1 + SettingsService.getVatRate());
}

export function formatPrice(price, language) {
  return price.toLocaleString(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const PaymentService = {
  calculateProratedAmount: (monthlyPrice, dateFrom) => {
    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const remainingDays =
      PaymentService.calculateProratedAmountInDays(dateFrom);
    const dailyRate = monthlyPrice / daysInMonth;
    return dailyRate * remainingDays + monthlyPrice;
  },

  calculateProratedAmountOnly: (monthlyPrice, dateFrom) => {
    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const remainingDays =
      PaymentService.calculateProratedAmountInDays(dateFrom);
    const dailyRate = monthlyPrice / daysInMonth;
    return dailyRate * remainingDays;
  },

  calculateProratedAmountInDays: dateFrom => {
    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const dayOfMonth = startDate.getDate();
    return daysInMonth - dayOfMonth + 1;
  },

  async getOrderFinalizationData(
    paymentIntentId,
    orderId,
    invoiceId,
    countryShortName
  ) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/GetOrderFinalizationSessionData`,
        {
          method: 'POST',
          body: JSON.stringify({
            PaymentIntentId: paymentIntentId,
            OrderId: orderId,
            InvoiceId: invoiceId,
            CountryShortName: countryShortName,
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create checkout session');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  async createCheckoutSession({ orderId, user, date, currentLocation }) {
    /** @type {RentSelfStorageContainerRequest} */
    const data = {
      orderId,
      countryShortName: SettingsService.getCountryShortCode(),
      locationName: currentLocation.name,
      dateFrom: DateService.formatDate(date),
      userEmail: user.email,
      userId: user.stripeCustomerId,
    };

    try {
      const token = AuthenticationService.getToken();
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateCheckoutSession`,
        {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create checkout session');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  async createVerificationSession(
    userEmail,
    titanEmail,
    orderId,
    rsItemId,
    dateFrom,
    debtorId,
    locationName,
    stripeUserId
  ) {
    try {
      const countryShortName = SettingsService.getCountryShortCode();
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/VerifyUserDocumentation`,
        {
          method: 'POST',
          body: JSON.stringify({
            UserEmail: userEmail,
            TitanEmail: titanEmail,
            OrderId: orderId,
            CountryShortName: countryShortName,
            RsItemId: rsItemId,
            DateFrom: dateFrom,
            DebtorId: debtorId,
            LocationName: locationName,
            StripeCustomerId: stripeUserId,
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      let responseData = await response.json();
      if (!response.ok) {
        const errorText =
          responseData?.message || 'Failed to create verification session';
        throw new Error(errorText);
      }
      return {
        clientSecret: responseData.clientSecret,
        verificationSessionId: responseData.verificationSessionId,
      };
    } catch (error) {
      throw error;
    }
  },

  async checkVerificationSessionStatus(verificationSessionId, userEmail) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CheckVerifyUserIdentityStatus`,
        {
          method: 'POST',
          body: JSON.stringify({
            CountryShortName: SettingsService.getCountryShortCode(),
            VerificationSessionId: verificationSessionId,
            UserEmail: userEmail,
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  async triggerVerificationTimeoutEmail({ orderId, user, titanEmail }) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/sendVerificationElapsedEmail`,
        {
          method: 'POST',
          body: JSON.stringify({
            TitanEmail: titanEmail,
            CustomerName:
              `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            OrderNumber: String(orderId),
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || 'Failed to trigger verification timeout email'
        );
      }

      const text = await response.text();
      return text ? text : { success: true };
    } catch (error) {
      throw error;
    }
  },

  /**
   * @param {CreateDebtorRequest} user
   * @returns {Promise<UnicontaDebtorResponse>}
   */
  async GetOrCreateDebtorAccount(user) {
    try {
      const token = AuthenticationService.getToken();

      if (!token)
        throw new Error(
          'User is not authenticated so we cannot create a debtor account'
        );

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateDebtor`,
        {
          method: 'POST',
          body: JSON.stringify(user),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create debtor');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  /**
   * @param {CreateOrderBookingRequest} bookingRequest
   * @returns {Promise<CreateBookingOrderResponse>}
   */
  async CreateBookingOrder(bookingRequest) {
    try {
      const token = AuthenticationService.getToken();

      if (!token)
        throw new Error(
          'User is not authenticated so we cannot create a booking order'
        );

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateBookingOrder`,
        {
          method: 'POST',
          body: JSON.stringify(bookingRequest),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create booking order');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },
};

export default PaymentService;
